import { useMemo, useState } from 'react';
import type { ApproverDecision } from '@zevik/schemas';
import type { FindingEntry } from '../api/client';
import { emptyFilter, FILTER_BANDS, filterFindings, type ListFilter } from '../review/batch';
import { orderFindings } from '../review/order';
import { CONDITIONAL_EXPLANATION, CONDITIONAL_PREFIX, reportedBand } from '../review/vocabulary';
import { Band } from './Band';
import { BatchBar, type RecordBatch } from './BatchBar';

/**
 * The findings of this run, strongest first (docs/DECISIONS.md ZD-112).
 *
 * The order is `orderFindings` in `review/order.ts`, which the page's initial
 * selection reads too — one derivation, two consumers, since the first live
 * run showed the two disagreeing (ZD-123 §11, closed in ZD-124 §5).
 *
 * ## The population is the run, not what is reportable
 *
 * Every finding the newest analysis produced, whatever state it is in — a reviewer
 * has to decide on findings they are going to reject, so filtering to
 * `client_ready` would show an empty list at the moment the gate opens
 * (ZD-111 §3). `finding.state` is the QA review's verdict and is deliberately not
 * shown as though it were the reviewer's: what a reviewer has decided is the
 * standing decision, read from the review log.
 *
 * ## A selection, and one verdict for it (ZD-127 §3)
 *
 * Each row carries a checkbox beside the button that opens it, and the list
 * can be narrowed by band, by whether a decision stands, and by whether the
 * finding is conditional — each a fact the row already prints, so narrowing
 * reads nothing the list does not. *Select all shown* takes the narrowed
 * list. What happens to a selection is `BatchBar`'s: one verdict, one reason,
 * refused in the API's words before it is sent.
 *
 * A row whose standing decision was made in a group says so — *in a group* —
 * because the record answers *was this finding considered individually?* and
 * the screen must not hide the answer.
 *
 * ## The marker's explanation is gated on there being one
 *
 * The report explains `If confirmed:` only where a conditional finding exists,
 * because a declaration about an absent thing reads as an omission of it
 * (ZD-035 §2, one layer out). The same gate applies here, and the sentence is
 * composed from the shared constant rather than restated.
 */

interface Props {
  findings: readonly FindingEntry[];
  standing: ReadonlyMap<string, ApproverDecision>;
  selected: string | null;
  onSelect: (findingId: string) => void;
  /** Records a group decision; `null` where nothing can be recorded from here (a fixture). */
  onRecordBatch: RecordBatch | null;
}

export function FindingList({
  findings,
  standing,
  selected,
  onSelect,
  onRecordBatch,
}: Props): React.JSX.Element {
  const anyConditional = findings.some((entry) => entry.finding.conditional.is_conditional);
  const decided = findings.filter((entry) => standing.has(entry.finding_key)).length;

  const [filter, setFilter] = useState<ListFilter>(emptyFilter);
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());

  const ordered = useMemo(() => orderFindings(findings), [findings]);
  const shown = useMemo(
    () => filterFindings(ordered, standing, filter, (entry) => reportedBand(entry.finding)),
    [ordered, standing, filter],
  );
  const selection = useMemo(
    () => ordered.filter((entry) => picked.has(entry.finding.finding_id)),
    [ordered, picked],
  );

  const toggle = (findingId: string): void => {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(findingId)) next.delete(findingId);
      else next.add(findingId);
      return next;
    });
  };
  const allShownPicked = shown.length > 0 && shown.every((e) => picked.has(e.finding.finding_id));

  return (
    <nav className="index" aria-label="findings">
      <div className="index__head">
        <strong>
          {String(findings.length)} findings · {String(decided)} decided
        </strong>
        {anyConditional ? <p className="index__note">{CONDITIONAL_EXPLANATION}</p> : null}

        <div className="filters" aria-label="narrow the list">
          <label className="filters__field">
            <span>Band</span>
            <select
              value={filter.band}
              onChange={(event) => {
                const band = event.target.value as ListFilter['band'];
                setFilter((current) => ({ ...current, band }));
              }}
            >
              <option value="any">any</option>
              {FILTER_BANDS.map((band) => (
                <option key={band} value={band}>
                  {band}
                </option>
              ))}
            </select>
          </label>
          <label className="filters__field">
            <span>Decision</span>
            <select
              value={filter.standing}
              onChange={(event) => {
                const value = event.target.value as ListFilter['standing'];
                setFilter((current) => ({ ...current, standing: value }));
              }}
            >
              <option value="any">any</option>
              <option value="undecided">not yet decided</option>
              <option value="decided">decided</option>
            </select>
          </label>
          <label className="filters__field">
            <span>Conditional</span>
            <select
              value={filter.conditional}
              onChange={(event) => {
                const value = event.target.value as ListFilter['conditional'];
                setFilter((current) => ({ ...current, conditional: value }));
              }}
            >
              <option value="any">any</option>
              <option value="conditional">conditional</option>
              <option value="unconditional">not conditional</option>
            </select>
          </label>
          <span className="filters__count">
            {shown.length === findings.length
              ? 'all shown'
              : `${String(shown.length)} of ${String(findings.length)} shown`}
          </span>
          <button
            type="button"
            className="submit--link"
            disabled={shown.length === 0}
            onClick={() => {
              setPicked((current) => {
                const next = new Set(current);
                for (const entry of shown) {
                  if (allShownPicked) next.delete(entry.finding.finding_id);
                  else next.add(entry.finding.finding_id);
                }
                return next;
              });
            }}
          >
            {allShownPicked ? 'Clear the shown' : 'Select all shown'}
          </button>
        </div>
      </div>

      {/*
       * Below the sticky head, not inside it. Walked in the browser with a
       * selection of nineteen, the bar inside the head made it 883 px tall and
       * every row sat underneath it: a click on a row hit the bar's legend.
       * The count and the filters stay pinned; the bar scrolls with the list.
       */}
      {selection.length === 0 ? null : (
        <BatchBar
          selection={selection}
          standing={standing}
          onRecord={onRecordBatch}
          onSelect={onSelect}
          onClear={() => {
            setPicked(new Set());
          }}
        />
      )}

      {shown.length === 0 ? (
        <p className="index__empty">Nothing matches this narrowing. Widen a filter.</p>
      ) : null}
      {shown.map((entry) => {
        const decision = standing.get(entry.finding_key);
        const id = entry.finding.finding_id;
        return (
          <div className="rowline" key={id} data-picked={picked.has(id)}>
            <input
              type="checkbox"
              className="rowline__pick"
              aria-label={`Select ${entry.finding.title}`}
              checked={picked.has(id)}
              onChange={() => {
                toggle(id);
              }}
            />
            <button
              type="button"
              className="row"
              aria-current={selected === id}
              onClick={() => {
                onSelect(id);
              }}
            >
              <span className="row__title">
                {entry.finding.conditional.is_conditional ? (
                  <span className="marker">{CONDITIONAL_PREFIX} </span>
                ) : null}
                {entry.finding.title}
              </span>
              <span className="row__meta">
                <Band band={reportedBand(entry.finding)} />
                {decision === undefined ? (
                  <span>not yet decided</span>
                ) : (
                  <span className="row__standing" data-disposition={decision.disposition}>
                    {decision.disposition}
                    {decision.batch_id === null ? '' : ' · in a group'}
                  </span>
                )}
              </span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}
